import apiClient from "../services/apiClient";
import { useAuthStoreRefactor } from "./authStoreRefactor";
import { useActionCableDebugStore } from "./actionCableDebugStore";

/**
 * Creates a simplified, efficient base store with caching and real-time updates
 * @param {Object} options - Configuration options
 * @param {String} options.endpoint - Base API endpoint
 * @param {String} options.name - Store name for logging
 * @param {String} options.objectName - Object name for success messages (e.g., "Account", "DayReport")
 * @param {Boolean} options.enableCache - Enable 5-minute caching (default: false)
 * @param {String} options.actionCableChannel - ActionCable channel name (optional)
 * @param {Boolean} options.infiniteLoading - Enable infinite loading support (default: false)
 * @returns {Object} - Base store configuration
 */
export function createBaseStoreRefactor(options) {
  const {
    endpoint,
    name,
    objectName,
    enableCache = false,
    actionCableChannel,
    infiniteLoading = false,
  } = options;

  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  return {
    state: () => ({
      data: [],
      loading: false,
      error: null,
      // Caching
      lastFetched: null,
      cacheEnabled: enableCache,
      dataVersion: 0, // Version counter for reactivity
      // ActionCable
      subscription: null,
      subscribed: false,
      actionCableChannel: actionCableChannel, // Expose the channel name
      // Infinite loading
      infiniteLoading: infiniteLoading, // Expose the configuration
      hasMoreData: true,
      currentOffset: 0,
      pageSize: 25,
      // Pagination metadata
      meta: null,
      // Internal state
      pendingRequests: new Map(),
      storeName: name, // Store the name for debugging
      // Request versioning for race condition prevention
      requestVersion: 0, // Incremented on each new request
      activeAbortControllers: new Map(), // Track AbortControllers for cancellation
    }),

    getters: {
      /**
       * Check if data should be refetched based on cache settings
       */
      shouldRefetch: (state) => {
        if (!state.cacheEnabled) return true;
        if (!state.lastFetched) return true;
        if (state.data.length === 0) return true;
        return Date.now() - state.lastFetched > CACHE_DURATION;
      },

      /**
       * Get item by ID
       */
      getById: (state) => (id) => {
        return state.data.find((item) => item.id == id);
      },

      /**
       * Check if currently loading
       */
      isLoading: (state) => state.loading,

      /**
       * Check if there's an error
       */
      hasError: (state) => !!state.error,

      /**
       * Get pagination metadata
       */
      pagination: (state) => state.meta?.pagination || null,

      /**
       * Check if there are more pages to load
       */
      hasMorePages: (state) =>
        state.meta?.pagination?.has_next_page || state.hasMoreData,

      /**
       * Get total count of items
       */
      totalCount: (state) => state.meta?.pagination?.total_count || null,

      /**
       * Get current page number
       */
      currentPage: (state) => state.meta?.pagination?.current_page || 1,

      /**
       * Get total pages
       */
      totalPages: (state) => state.meta?.pagination?.total_pages || null,
    },

    actions: {
      /**
       * Initialize store (setup ActionCable if configured)
       */
      async init() {
        // Register this store with debug store for global control
        const debugStore = useActionCableDebugStore();
        debugStore.registerStore(this);

        if (actionCableChannel) {
          this.subscribeToActionCable();
        }
      },

      /**
       * Fetch all data with optional caching and infinite loading support
       * @param {Object} params - Query parameters
       * @param {Boolean} forceRefresh - Force bypass cache
       * @param {Boolean} append - Append to existing data (for infinite loading)
       * @param {String} customEndpoint - Optional custom endpoint to use instead of the default
       * @returns {Promise} - Resolves with { data, requestVersion } for version checking
       */
      async fetchAll(
        params = {},
        forceRefresh = false,
        append = false,
        customEndpoint = null,
      ) {
        // Handle caching for non-infinite loading stores
        if (!infiniteLoading && !forceRefresh && !this.shouldRefetch) {
          console.log(`${name}: Using cached data`);
          return this.data;
        }

        // Handle infinite loading offset
        if (infiniteLoading && append) {
          params.offset = this.currentOffset;
        }

        // Use custom endpoint if provided, otherwise use default
        const apiEndpoint = customEndpoint || endpoint;

        // Check if this is a search endpoint
        const isSearchEndpoint = apiEndpoint.includes("/search");

        // Prevent duplicate requests
        const requestKey = JSON.stringify({ params, endpoint: apiEndpoint });
        if (this.pendingRequests.has(requestKey)) {
          return this.pendingRequests.get(requestKey);
        }

        // 🔒 Increment request version and create AbortController for this request
        this.requestVersion += 1;
        const thisRequestVersion = this.requestVersion;
        const abortController = new AbortController();
        this.activeAbortControllers.set(requestKey, abortController);

        this.loading = true;
        this.error = null;

        let requestPromise;

        // Get the API client
        const client = apiClient;

        if (isSearchEndpoint) {
          // For search endpoints, use POST with q in body and offset as URL param
          const { q, offset = 0, ...otherParams } = params;
          const searchBody = { q: q || otherParams };
          const urlParams = { offset };

          requestPromise = client.post(
            apiEndpoint,
            searchBody,
            { params: urlParams, signal: abortController.signal },
            { showErrorAlert: false, showSuccessAlert: false },
          );
        } else {
          // For regular endpoints, use GET with params
          requestPromise = client.get(
            apiEndpoint,
            { params, signal: abortController.signal },
            { showErrorAlert: false },
          );
        }

        requestPromise = requestPromise
          .then((response) => {
            // 🔒 RACE CONDITION FIX: Check if this request is still valid
            if (thisRequestVersion !== this.requestVersion) {
              console.log(
                `${name}: Ignoring stale response (version ${thisRequestVersion} vs current ${this.requestVersion})`,
              );
              return []; // Return empty but don't update state
            }

            // Handle new API response format with meta data
            let newData, meta;

            if (response && typeof response === "object" && response.data) {
              // New format: { data: [...], meta: {...}, status: 'success' }
              newData = response.data;
              meta = response.meta;

              console.log(`${name}: Received paginated response:`, {
                dataCount: newData.length,
                meta: meta?.pagination,
                status: response.status,
              });
            } else if (Array.isArray(response)) {
              // Legacy format: [...]
              newData = response;
              meta = null;
              console.log(
                `${name}: Received legacy array response with ${newData.length} items`,
              );
            } else {
              console.warn(`${name}: Unexpected response format:`, response);
              newData = [];
              meta = null;
            }

            if (infiniteLoading) {
              if (append && this.data.length > 0) {
                // Append new data, avoiding duplicates
                this._mergeData(newData);
                this.currentOffset += newData.length;
              } else {
                // Fresh load
                this.data = newData;
                this.currentOffset = newData.length;
              }

              // Update hasMoreData based on meta information or fallback to length check
              if (meta?.pagination) {
                this.hasMoreData = meta.pagination.has_next_page;
                this.currentOffset =
                  meta.pagination.offset +
                  meta.pagination.count_on_current_page;
                console.log(
                  `${name}: Updated from meta - hasMoreData: ${this.hasMoreData}, offset: ${this.currentOffset}`,
                );
              } else {
                // Fallback: assume no more data if we got less than page size
                this.hasMoreData = newData.length >= this.pageSize;
                console.log(
                  `${name}: Updated from length check - hasMoreData: ${this.hasMoreData}`,
                );
              }
            } else {
              // Standard loading
              this.data = newData;
            }

            // Store metadata
            this.meta = meta;
            this.lastFetched = Date.now();

            return newData;
          })
          .catch((error) => {
            // 🔒 Don't report errors for aborted requests
            if (error.name === "AbortError" || error.name === "CanceledError") {
              console.log(`${name}: Request was cancelled`);
              return [];
            }
            this.error = error.message || "Er is een fout opgetreden";
            console.error(`${name}: Error fetching data:`, error);
            throw error;
          })
          .finally(() => {
            this.loading = false;
            this.pendingRequests.delete(requestKey);
            this.activeAbortControllers.delete(requestKey);
          });

        this.pendingRequests.set(requestKey, requestPromise);
        return requestPromise;
      },

      /**
       * Load more data (for infinite loading)
       * @param {Object} params - Query parameters
       * @param {String} customEndpoint - Optional custom endpoint to use
       */
      async loadMore(params = {}, customEndpoint = null) {
        if (!infiniteLoading || !this.hasMoreData || this.loading) {
          return [];
        }
        return this.fetchAll(params, false, true, customEndpoint);
      },

      /**
       * Backward compatibility method - alias for fetchAll
       * @param {Boolean} forceRefresh - Force bypass cache
       * @param {Object} params - Query parameters
       */
      async getAll(forceRefresh = false, params = {}) {
        return this.fetchAll(params, forceRefresh);
      },

      /**
       * Generic search method that uses the search endpoint
       * @param {Object} searchParams - Search parameters
       * @param {Boolean} append - Whether to append to existing results (for infinite loading)
       */
      async search(searchParams = {}, append = false) {
        const searchQuery = searchParams.q || searchParams;
        return this.fetchAll(
          { q: searchQuery },
          false,
          append,
          `${endpoint}/search`,
        );
      },

      /**
       * Fetch single item by ID
       * @param {Number|String} id - Item ID
       */
      async fetchById(id) {
        if (!id) {
          throw new Error(`Cannot fetch ${name} without an ID`);
        }

        // CHECK CACHE FIRST!
        const existingItem = this.getById(id);
        if (existingItem && this.cacheEnabled && !this.shouldRefetch) {
          // 🔍 SMART CACHE: Check if we have complete data for detail view
          const isCompleteData = this._isCompleteDetailData(existingItem);
          if (isCompleteData) {
            console.log(
              `${name}: Using cached item ${id} (complete data) and refreshing in background`,
            );
            // Stale-while-revalidate: kick off a background refresh without toggling loading UI
            (async () => {
              try {
                const client = apiClient;
                const fresh = await client.get(
                  `${endpoint}/${id}`,
                  {},
                  { showErrorAlert: false },
                );
                this._upsertItem(fresh);
                this.lastFetched = Date.now();
              } catch (err) {
                console.warn(
                  `${name}: Background refresh failed for ${id}:`,
                  err?.message || err,
                );
              }
            })();
            return existingItem;
          } else {
            console.log(
              `${name}: Cached item ${id} has partial data, fetching complete version`,
            );
            // Continue to fetch complete data
          }
        }

        // Prevent duplicate requests
        const requestKey = `fetchById_${id}`;
        if (this.pendingRequests.has(requestKey)) {
          return this.pendingRequests.get(requestKey);
        }

        this.loading = true;
        this.error = null;

        const requestPromise = (async () => {
          try {
            const client = apiClient;
            const data = await client.get(
              `${endpoint}/${id}`,
              {},
              { showErrorAlert: false },
            );
            this._upsertItem(data);
            this.lastFetched = Date.now(); // UPDATE CACHE TIMESTAMP
            return data;
          } catch (error) {
            this.error =
              error.message || "Er is een fout opgetreden bij het ophalen";
            console.error(`${name}: Error fetching item ${id}:`, error);
            throw error;
          } finally {
            this.loading = false;
            this.pendingRequests.delete(requestKey);
          }
        })();

        this.pendingRequests.set(requestKey, requestPromise);
        return requestPromise;
      },

      /**
       * Create new item with enhanced logging
       */
      async create(item, successMessage) {
        console.log(`🔧 ${name}: Creating new item:`, item);
        this.loading = true;
        this.error = null;

        try {
          const client = apiClient;
          const message =
            successMessage ||
            (objectName ? `${objectName} is succesvol aangemaakt` : undefined);
          const data = await client.post(endpoint, item, {}, message);

          console.log(`🔧 ${name}: Create API response:`, data);
          this._upsertItem(data, true); // Add to beginning
          console.log(
            `🔧 ${name}: Item added to local store, new count: ${this.data.length}`,
          );

          return data;
        } catch (error) {
          this.error =
            error.message || "Er is een fout opgetreden bij het aanmaken";
          console.error(`${name}: Error creating item:`, error);
          throw error;
        } finally {
          this.loading = false;
        }
      },

      /**
       * Update existing item with enhanced logging
       */
      async update(item, successMessage) {
        if (!item?.id) {
          throw new Error(`Cannot update ${name} without an ID`);
        }

        console.log(`🔧 ${name}: Updating item ${item.id}:`, item);
        this.loading = true;
        this.error = null;

        try {
          const client = apiClient;
          const message =
            successMessage ||
            (objectName ? `${objectName} is succesvol bijgewerkt` : undefined);
          const data = await client.patch(
            `${endpoint}/${item.id}`,
            item,
            {},
            message,
          );

          console.log(`🔧 ${name}: Update API response:`, data);
          this._upsertItem(data);
          console.log(`🔧 ${name}: Item updated in local store`);

          return data;
        } catch (error) {
          this.error =
            error.message || "Er is een fout opgetreden bij het bijwerken";
          console.error(`${name}: Error updating item ${item.id}:`, error);
          throw error;
        } finally {
          this.loading = false;
        }
      },

      /**
       * Delete item with enhanced logging
       */
      async destroy(itemOrId, successMessage) {
        const id = typeof itemOrId === "object" ? itemOrId.id : itemOrId;
        if (!id) {
          throw new Error(`Cannot delete ${name} without an ID`);
        }

        console.log(`🔧 ${name}: Deleting item ${id}`);
        this.loading = true;
        this.error = null;

        try {
          const client = apiClient;
          const message =
            successMessage ||
            (objectName ? `${objectName} is succesvol verwijderd` : undefined);
          await client.delete(`${endpoint}/${id}`, {}, message);

          console.log(`🔧 ${name}: Delete API success for item ${id}`);
          this._removeItem(id);
          console.log(
            `🔧 ${name}: Item removed from local store, new count: ${this.data.length}`,
          );

          return { success: true };
        } catch (error) {
          this.error =
            error.message || "Er is een fout opgetreden bij het verwijderen";
          console.error(`${name}: Error deleting item ${id}:`, error);
          throw error;
        } finally {
          this.loading = false;
        }
      },

      /**
       * Custom API call (for special endpoints like activate/deactivate)
       * @param {String} method - HTTP method
       * @param {String} url - URL path (can be relative to endpoint)
       * @param {Object} data - Request data
       * @param {String} successMessage - Custom success message
       */
      async customApiCall(method, url, data = {}, successMessage) {
        this.loading = true;
        this.error = null;

        try {
          const fullUrl = url.startsWith("/") ? url : `${endpoint}/${url}`;
          const client = apiClient;

          let result;
          switch (method.toUpperCase()) {
            case "GET":
              result = await client.get(fullUrl, {}, successMessage);
              break;
            case "POST": {
              // For POST, wrap data in { data: ... } for tests expecting this format
              const postData = data instanceof FormData ? data : { data };
              result = await client.post(fullUrl, postData, {}, successMessage);
              break;
            }
            case "PATCH": {
              // For PATCH with FormData, pass it directly, otherwise wrap
              const patchData =
                data instanceof FormData
                  ? { data, headers: { "Content-Type": "multipart/form-data" } }
                  : data;
              result = await client.patch(
                fullUrl,
                patchData,
                {},
                successMessage,
              );
              break;
            }
            case "DELETE":
              result = await client.delete(fullUrl, {}, successMessage);
              break;
            default:
              throw new Error(`Unsupported method: ${method}`);
          }

          // If response contains data that looks like our item, update it
          if (result?.id) {
            this._upsertItem(result);
          }

          return result;
        } catch (error) {
          this.error = error.message || "Er is een fout opgetreden";
          console.error(`${name}: Error in custom API call:`, error);
          throw error;
        } finally {
          this.loading = false;
        }
      },

      /**
       * Force refresh data (bypass cache)
       */
      async refresh() {
        if (infiniteLoading) {
          this.currentOffset = 0;
          this.hasMoreData = true;
        }
        return this.fetchAll({}, true);
      },

      /**
       * Cancel all active requests
       * Call this when a component unmounts to prevent stale responses from being processed
       */
      cancelAllRequests() {
        // Abort all active requests
        for (const [key, controller] of this.activeAbortControllers) {
          try {
            controller.abort();
            console.log(`${name}: Cancelled request ${key}`);
          } catch (e) {
            // Ignore abort errors
          }
        }
        this.activeAbortControllers.clear();
        this.pendingRequests.clear();

        // Increment request version to invalidate any in-flight responses
        this.requestVersion += 1;
        console.log(
          `${name}: Incremented request version to ${this.requestVersion}, all pending requests invalidated`,
        );
      },

      /**
       * Clear all data and reset state
       */
      clear() {
        // Cancel any pending requests first
        this.cancelAllRequests();

        this.data = [];
        this.error = null;
        this.lastFetched = null;
        this.loading = false; // Reset loading state
        if (infiniteLoading) {
          this.currentOffset = 0;
          this.hasMoreData = true;
        }
      },

      /**
       * Subscribe to ActionCable for real-time updates
       */
      async subscribeToActionCable() {
        if (!actionCableChannel || this.subscribed) return;

        const debugStore = useActionCableDebugStore();

        // Check if ActionCable is disabled
        if (!debugStore.isEnabled) {
          console.log(
            `${name}: ActionCable disabled, skipping subscription to ${actionCableChannel}`,
          );
          return;
        }

        try {
          console.log(
            `${name}: Subscribing to ActionCable channel ${actionCableChannel}`,
          );

          // Lazily import the cable service to avoid a module-init circular
          // import (cable -> tenantStore -> baseStoreRefactor). The consumer is
          // only needed at runtime, well after all modules have initialised.
          const { default: getDefaultConsumer, createAuthenticatedConsumer } =
            await import("../services/cable");

          const authStore = useAuthStoreRefactor();
          const activeCable = authStore.token
            ? createAuthenticatedConsumer(authStore.token)
            : getDefaultConsumer();

          this.subscription = activeCable.subscriptions.create(
            actionCableChannel,
            {
              connected: () => {
                console.log(`${name}: ✅ Connected to ${actionCableChannel}`);
                this.subscribed = true;
                debugStore.updateConnectionStatus(name, true);
              },

              disconnected: () => {
                console.log(
                  `${name}: ❌ Disconnected from ${actionCableChannel}`,
                );
                this.subscribed = false;
                debugStore.updateConnectionStatus(name, false);
              },

              received: (data) => {
                console.log(`${name}: 📨 Received ActionCable data:`, data);
                debugStore.logMessage(actionCableChannel, data);
                this._handleActionCableMessage(data);
              },
            },
          );

          // Register connection with debug store
          debugStore.registerConnection(
            name,
            actionCableChannel,
            this.subscription,
          );
        } catch (error) {
          console.error(`${name}: Error subscribing to ActionCable:`, error);
          throw error;
        }
      },

      /**
       * Unsubscribe from ActionCable
       */
      unsubscribeFromActionCable() {
        if (this.subscription) {
          try {
            console.log(`${name}: 🔌 Unsubscribing from ActionCable`);
            this.subscription.unsubscribe();
          } catch (error) {
            console.error(`${name}: Error unsubscribing:`, error);
          } finally {
            this.subscription = null;
            this.subscribed = false;

            // Update debug store
            const debugStore = useActionCableDebugStore();
            debugStore.updateConnectionStatus(name, false);
          }
        }
      },

      /**
       * Subscribe to updates (alias for subscribeToActionCable)
       */
      subscribeToUpdates() {
        return this.subscribeToActionCable();
      },

      /**
       * Unsubscribe from updates (alias for unsubscribeFromActionCable)
       */
      unsubscribeFromUpdates() {
        return this.unsubscribeFromActionCable();
      },

      /**
       * Add or update item (used by ActionCable for real-time updates)
       */
      addOrUpdate(entity) {
        console.log(`${name}: addOrUpdate called with entity:`, {
          id: entity?.id,
          type: typeof entity,
        });
        if (entity?.id) {
          this._upsertItem(entity);
        } else {
          console.warn(
            `${name}: addOrUpdate called with invalid entity (no ID):`,
            entity,
          );
        }
      },

      /**
       * Remove item (used by ActionCable for real-time updates)
       */
      remove(id) {
        console.log(`${name}: remove called with ID:`, id);
        if (id) {
          this._removeItem(id);
        } else {
          console.warn(`${name}: remove called with invalid ID:`, id);
        }
      },

      // Internal helper methods

      /**
       * Add or update item in data array
       * @private
       */
      _upsertItem(item, prepend = false) {
        if (!item?.id) {
          console.log(
            `🔧 ${name}: _upsertItem called with invalid item (no ID):`,
            item,
          );
          return;
        }

        console.log(
          `🔧 ${name}: _upsertItem called for item ${item.id}, prepend: ${prepend}`,
        );
        console.log(
          `🔧 ${name}: Current store data length: ${this.data.length}`,
        );

        const index = this.data.findIndex(
          (existing) => existing.id === item.id,
        );
        console.log(`🔧 ${name}: Found existing item at index: ${index}`);

        if (index !== -1) {
          // Update existing (preserve unspecified keys by merging with current item)
          const oldItem = this.data[index];
          console.log(`🔧 ${name}: Updating existing item ${item.id}:`, {
            old: {
              sticky: oldItem.sticky,
              description: oldItem.description?.slice(0, 50),
            },
            new: {
              sticky: item.sticky,
              description: item.description?.slice(0, 50),
            },
          });

          // Filter out undefined values to preserve old data
          const filteredItem = Object.entries(item).reduce(
            (acc, [key, value]) => {
              if (value !== undefined) {
                acc[key] = value;
              }
              return acc;
            },
            {},
          );

          this.data[index] = { ...oldItem, ...filteredItem };
        } else {
          // Add new
          console.log(`🔧 ${name}: Adding new item ${item.id}`);
          if (prepend) {
            this.data.unshift({ ...item });
          } else {
            this.data.push({ ...item });
          }
        }

        console.log(`🔧 ${name}: New store data length: ${this.data.length}`);
      },

      /**
       * Remove item from data array
       * @private
       */
      _removeItem(id) {
        this.data = this.data.filter((item) => item.id !== id);
      },

      /**
       * Check if cached item has complete detail data vs partial search data
       * @private
       */
      _isCompleteDetailData(item) {
        if (!item) return false;

        // For AccountsStore, check for fields that are only present in detail view
        if (name.includes("Account")) {
          return !!(
            Object.prototype.hasOwnProperty.call(item, "selected_traits") &&
            Object.prototype.hasOwnProperty.call(item, "attachments") &&
            Object.prototype.hasOwnProperty.call(item, "deadlines") &&
            Object.prototype.hasOwnProperty.call(item, "description") &&
            Object.prototype.hasOwnProperty.call(item, "times_seen")
          );
        }

        // For other stores, assume cached data is complete (can be enhanced per store type)
        return true;
      },

      /**
       * Merge new data with existing data (for infinite loading)
       * @private
       */
      _mergeData(newData) {
        newData.forEach((newItem) => {
          const existingIndex = this.data.findIndex(
            (item) => item.id === newItem.id,
          );
          if (existingIndex !== -1) {
            // Update existing item
            this.data[existingIndex] = { ...newItem };
          } else {
            // Add new item
            this.data.push({ ...newItem });
          }
        });
      },

      /**
       * Handle ActionCable messages
       * @private
       */
      _handleActionCableMessage(data) {
        console.log(`🎯 ${name}: _handleActionCableMessage called with:`, data);

        // Handle standard format: { action: 'created/updated/destroyed', data: {...} }
        if (data.action) {
          const itemData = data.data || data;
          console.log(
            `🎯 ${name}: Processing action "${data.action}" for item:`,
            { id: itemData.id, type: typeof itemData },
          );

          switch (data.action) {
            case "created":
            case "updated":
              console.log(`🎯 ${name}: Upserting item ${itemData.id}`);
              this._upsertItem(itemData);
              break;
            case "destroyed":
              console.log(
                `🎯 ${name}: Removing item ${data.id || itemData.id}`,
              );
              this._removeItem(data.id || itemData.id);
              break;
            default:
              console.warn(
                `${name}: Unknown ActionCable action: ${data.action}`,
              );
          }
          return;
        }

        // Handle direct item data (some channels send this)
        if (data.id) {
          console.log(
            `🎯 ${name}: Processing direct item data for ID ${data.id}`,
          );
          this._upsertItem(data);
          return;
        }

        // Handle wrapped data (e.g., { notification: {...} })
        for (const key of Object.keys(data)) {
          if (data[key]?.id) {
            console.log(
              `🎯 ${name}: Processing wrapped data "${key}" for ID ${data[key].id}`,
            );
            this._upsertItem(data[key]);
            return;
          }
        }

        console.warn(`${name}: Unknown ActionCable message format:`, data);
      },
    },
  };
}
